<template>
    <tip-tap-dropdown>
        <template #button>
            <tip-tap-button
                class="!w-[100px] flex justify-between gap-1"
                :tooltip="lang('fields::fields.wysiwyg.font.tooltip')"
            >
                <span class="text-xs truncate">
                    {{ activeFontFamily || lang('fields::fields.wysiwyg.font.default') }}
                </span>
                <div class="absolute flex flex-col -translate-y-1/2 right-px top-1/2">
                    <icon icon="fa/solid/caret-down" class="w-2 h-2"></icon>
                </div>
            </tip-tap-button>
        </template>
        <template #dropdown="{ close }">
            <ui-popover-section>
                <ui-popover-segment>
                    <div class="p-1">
                        <input
                            v-model="search"
                            type="text"
                            class="w-full px-2 py-1 text-xs border rounded border-neutral-300 dark:border-neutral-600 dark:bg-neutral-800 focus:outline-hidden"
                            :placeholder="lang('fields::fields.wysiwyg.font.search')"
                        />
                    </div>
                </ui-popover-segment>
                <ui-popover-segment>
                    <div class="overflow-y-auto max-h-[200px]">
                        <ui-popover-link
                            @click="
                                unsetFont();
                                close();
                            "
                        >
                            <span class="text-xs">{{ lang('fields::fields.wysiwyg.font.unset') }}</span>
                        </ui-popover-link>
                        <ui-popover-link
                            v-for="font in filteredFonts"
                            :key="font.family"
                            :active="activeFontFamily === font.family"
                            @click="
                                selectFont(font);
                                close();
                            "
                        >
                            <span class="text-xs" :style="{ fontFamily: font.family }">{{ font.family }}</span>
                        </ui-popover-link>
                    </div>
                </ui-popover-segment>
            </ui-popover-section>
        </template>
    </tip-tap-dropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import type { Editor } from '@tiptap/vue-3';
import type { FontInfo } from '@src/Fields/Pages/WYSIWYG/utils/wysiwygFonts';
import { injectFontStylesheets } from '@src/Fields/Pages/WYSIWYG/utils/wysiwygFonts';
import TipTapDropdown from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapDropdown.vue';
import TipTapButton from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapButton.vue';
import Icon from '@src/Common/Components/Icon.vue';

const props = defineProps<{
    editor: Editor;
}>();

const search = ref('');
const fonts = ref<FontInfo[]>([]);

let fontListCache: FontInfo[] | null = null;

const activeFontFamily = computed(() => {
    return props.editor.getAttributes('textStyle').fontFamily || '';
});

const filteredFonts = computed(() => {
    if (!search.value) {
        return fonts.value;
    }

    const query = search.value.toLowerCase();

    return fonts.value.filter((font) => font.family.toLowerCase().includes(query));
});

const selectFont = (font: FontInfo) => {
    props.editor.chain().focus().setFontFamily(font.family).run();
    injectFontStylesheets([font]);
};

const unsetFont = () => {
    props.editor.chain().focus().unsetFontFamily().run();
};

const loadFonts = async () => {
    if (fontListCache) {
        fonts.value = fontListCache;

        return;
    }

    try {
        const response = await fetch(route('fields.fonts.list'));
        const data = await response.json();
        fontListCache = data;
        fonts.value = data;
    } catch {
        fonts.value = [];
    }
};

onMounted(() => {
    loadFonts();
});
</script>
